import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Calendar,
  LogOut,
  Search,
  FolderOpen,
  Briefcase,
  Building2,
  FileText,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  BriefcaseBusiness,
} from "lucide-react";
import strings from "../../../utils/Strings";
import { useAuthContext } from "../../../features/auth/data/context/AuthContext";
import Alert from "../Alert";
import Loader from "../Loader";
import "./styles/LayoutStyles.css";

const navGroups = [
  {
    name: "Dashboard",
    isStandalone: true,
    path: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    name: "Applicant",
    icon: Users,
    items: [
      { name: "Portfolios", path: "/dashboard/portfolio", icon: FolderOpen },
      { name: "Interviews", path: "/dashboard/interviews", icon: Calendar },
      { name: "Assessments", path: "/dashboard/assessments", icon: FileText },
      { name: "Question Bank", path: "/dashboard/question-bank", icon: HelpCircle },
    ],
  },
  {
    name: "Recruiter",
    icon: Briefcase,
    items: [
      { name: "Talent Search", path: "/dashboard/job-search", icon: Search },
      { name: "Job Opportunities", path: "/dashboard/job-opportunities", icon: BriefcaseBusiness },
      { name: "Organizations", path: "/dashboard/organizations", icon: Building2 },
    ]
  }
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthContext();
  const [showLogoutAlert, setShowLogoutAlert] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Keep track of which groups are expanded.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    Applicant: false,
    Recruiter: false
  });

  const location = useLocation();

  React.useEffect(() => {
    setExpandedGroups(prev => {
      const next: Record<string, boolean> = { ...prev };
      navGroups.forEach(group => {
        if (!group.isStandalone && group.items) {
          const isActive = group.items.some(item => {
            let active = location.pathname.startsWith(item.path);
            if (item.path === '/dashboard/interviews' && location.pathname.startsWith('/dashboard/results')) {
              active = true;
            }
            if (item.path === '/dashboard/job-opportunities' && (location.pathname.startsWith('/dashboard/job-applications') || location.pathname.startsWith('/dashboard/job-opportunity'))) {
              active = true;
            }
            if (item.path === '/dashboard/portfolio' && location.pathname.startsWith('/dashboard/learners')) {
              active = true;
            }
            return active;
          });
          if (isActive) {
            next[group.name] = true;
          } else {
            next[group.name] = false;
          }
        }
      });
      return next;
    });
  }, [location.pathname]);

  const handleLogoutConfirm = async () => {
    setShowLogoutAlert(false);
    setIsLoggingOut(true);
    try {
      await logout();
      navigate("/");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  return (
    <aside className="sidebar">
      <Loader isLoading={isLoggingOut} message="Logging out..." />
      <Alert
        show={showLogoutAlert}
        type="warning"
        title="Logout Confirmation"
        message="Are you sure you want to log out of your session?"
        onClose={() => setShowLogoutAlert(false)}
        primaryButtonText="Logout"
        onPrimaryClick={handleLogoutConfirm}
        secondaryButtonText="Cancel"
        onSecondaryClick={() => setShowLogoutAlert(false)}
      />

      <nav className="sidebar-nav">
        {navGroups.map((group) => {
          if (group.isStandalone) {
            return (
              <NavLink
                key={group.name}
                to={group.path!}
                end={group.path === "/dashboard"}
                className={({ isActive }) => {
                  let isReallyActive = isActive;
                  const returnTo = location.state?.returnTo;
                  if (returnTo) {
                    if (group.path === "/dashboard") {
                      isReallyActive = returnTo === "/dashboard";
                    } else {
                      isReallyActive = returnTo.startsWith(group.path!);
                    }
                  }
                  return `sidebar-nav-item ${isReallyActive ? "active" : ""}`;
                }}
              >
                <div className="nav-icon">
                  <group.icon size={22} />
                </div>
                <span className="nav-text">{group.name}</span>
              </NavLink>
            );
          }

          // It's a collapsible group
          const isExpanded = expandedGroups[group.name];
          const isGroupActive = group.items?.some(item => {
            let active = location.pathname.startsWith(item.path);
            if (item.path === '/dashboard/interviews' && location.pathname.startsWith('/dashboard/results')) {
              active = true;
            }
            if (item.path === '/dashboard/job-opportunities' && (location.pathname.startsWith('/dashboard/job-applications') || location.pathname.startsWith('/dashboard/job-opportunity'))) {
              active = true;
            }
            if (item.path === '/dashboard/portfolio' && location.pathname.startsWith('/dashboard/learners')) {
              active = true;
            }
            return active;
          });

          return (
            <div key={group.name} className="sidebar-nav-group">
              <button
                className={`sidebar-nav-group-header ${isGroupActive ? 'active' : ''}`}
                onClick={() => toggleGroup(group.name)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div className="nav-icon">
                    <group.icon size={22} />
                  </div>
                  <span className="nav-text">{group.name}</span>
                </div>
                <div className="nav-text sidebar-group-chevron">
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </button>

              <div className={`sidebar-nav-sub-items ${isExpanded ? 'expanded' : ''}`}>
                {group.items?.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) => {
                      let isReallyActive = isActive || location.pathname.startsWith(item.path);
                      if (item.path === '/dashboard/interviews' && location.pathname.startsWith('/dashboard/results')) {
                        isReallyActive = true;
                      }
                      if (item.path === '/dashboard/job-opportunities' && (location.pathname.startsWith('/dashboard/job-applications') || location.pathname.startsWith('/dashboard/job-opportunity'))) {
                        isReallyActive = true;
                      }
                      if (item.path === '/dashboard/portfolio' && location.pathname.startsWith('/dashboard/learners')) {
                        isReallyActive = true;
                      }
                      const returnTo = location.state?.returnTo;
                      if (returnTo) {
                        isReallyActive = returnTo.startsWith(item.path);
                      }
                      return `sidebar-nav-item sub-item ${isReallyActive ? "active" : ""}`;
                    }}
                  >
                    <div className="nav-icon">
                      <item.icon size={18} />
                    </div>
                    <span className="nav-text">{item.name}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-footer-card powered-by-card">
          <div className="sidebar-footer-logo-section">
            <div className="nav-icon sidebar-collapsed-logo">
              <img src="/logo.png" alt="Logo" style={{ width: '36px', height: '36px', objectFit: 'contain' }} />
            </div>
            
            <div className="nav-text sidebar-expanded-logo">
              <span className="powered-by-title">POWERED BY</span>
              <img src="/logo_name_only.png" alt="JOZhrm" className="expanded-logo-img" />
              <span className="powered-by-subtitle">SMART HR • SMARTER WORKFORCE</span>
            </div>
          </div>
        </div>

        <div className="sidebar-footer-divider" />

        <div className="sidebar-footer-card logout-card">
          <button
            className="sidebar-logout-btn"
            onClick={() => setShowLogoutAlert(true)}
          >
            <div className="nav-icon">
              <LogOut size={22} />
            </div>
            <span className="nav-text">{strings.sidebar.logout}</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
